import React, { Fragment, useContext, useRef } from 'react';
import styles from './ReportListHeader.module.scss';
import { SFPeopleOption } from 'sfui';
import get from 'lodash.get';
import unset from 'lodash.unset';
import {
  ReportsCount,
  ReportsCountLabel
} from './ReportsCountLabel/ReportsCountLabel';
import { FilterByButton } from './FilterByButton/FilterByButton';
import {
  clearEmptyObjects,
  cloneObject,
  isValueEmpty
} from '../../../../Helpers';
import {
  formatDateString,
  UserGroup,
  upperFirstChar
} from 'ui-smartforce-settings';
import { FilterChip, FilterChipList } from './FilterChipList/FilterChipList';
import {
  DateRange,
  Field,
  FieldValueType,
  LocationAreaType,
  NumberRange,
  ReportFiltersConfig,
  ReportListFilters,
  ReportTag
} from '../../../../Models';
import { StateConfigContext } from '../../../../Context/StateConfigContext';
import { ReportSortGroupByButton } from '../../../../Components/ReportList/ReportSortGroupByButton/ReportSortGroupByButton';
import { ReportSortGroupByButtonMenuProps } from '../../../../Components/ReportList/ReportSortGroupByButton/ReportSortGroupByButtonMenu/ReportSortGroupByButtonMenu';
import { MoreOptionsButton } from '../../../../Components/MoreOptionsButton/MoreOptionsButton';
import { ReportSortGroupByButtonPopover } from '../../../../Components/ReportList/ReportSortGroupByButton/ReportSortGroupByButtonPopover/ReportSortGroupByButtonPopover';
import { SearchFilter } from '../SearchFilter/SearchFilter';

function getChipLabel(
  categoryName: string,
  value: FieldValueType,
  field: Field
) {
  if (field.component === 'tagsfield') {
    return `${field.label}: ${(value as ReportTag[])
      .map((t) => t.name)
      .join(', ')}`;
  }

  let label: string = `${categoryName} | `;

  if (field.component === 'daterange') {
    const dateRangeValue = value as DateRange;
    let dateLabel = formatDateString(
      dateRangeValue.dateFrom as string,
      'MM/DD/YYYY'
    );

    if (dateRangeValue.dateTo) {
      dateLabel = `From ${dateLabel} to ${formatDateString(
        dateRangeValue.dateTo as string,
        'MM/DD/YYYY'
      )}`;
    }

    label += dateLabel;
  } else if (field.component === 'numberrange') {
    const numberRange: NumberRange = value as NumberRange;
    label += `From ${numberRange.from} to ${numberRange.to}`;
  } else if (field.component === 'peoplepicker') {
    const peopleValue: SFPeopleOption = value as SFPeopleOption;
    label += `${field.label}: ${peopleValue.name}`;
  } else if (
    field.component === 'areasfield' ||
    field.component === 'groupsfield'
  ) {
    const listValue: (LocationAreaType | UserGroup)[] = value as (
      | LocationAreaType
      | UserGroup
    )[];
    label += `${field.label}: ${listValue
      .map((v: LocationAreaType | UserGroup) => v.name)
      .join(', ')}`;
  } else {
    label += `${field.label}: ${value}`;
  }

  return label;
}

function getChips(
  config: ReportFiltersConfig,
  filters: ReportListFilters
): FilterChip[] {
  if (isValueEmpty(filters)) {
    return [];
  }

  let chips: FilterChip[] = [];

  for (let categoryKey in config) {
    const categoryTitle = config[categoryKey].title;
    for (let section of config[categoryKey].sections) {
      for (let subsection of section.sub_sections) {
        for (let field of subsection.fields) {
          let fieldValue: FieldValueType;
          let path: string = field.name;

          if (subsection.name) {
            path = `${subsection.name}.${path}`;
          }

          if (section.name) {
            path = `${section.name}.${path}`;
          }

          path = `${categoryKey}.${path}`;

          fieldValue = get(filters, path);
          if (fieldValue) {
            const label = getChipLabel(categoryTitle, fieldValue, field);

            chips.push({
              label,
              onDelete: (filters: ReportListFilters) => {
                let newValue = cloneObject(filters);
                unset(newValue, path);
                clearEmptyObjects(newValue);
                return newValue;
              }
            });
          }
        }
      }
    }
  }

  return chips;
}

function getLabelSuffix(
  hasAnalytics: boolean,
  withoutFilters: boolean,
  withoutDateFilters: boolean
): string {
  let label = '';

  if (!hasAnalytics || withoutFilters) {
    label += 'REPORTS';
  }

  if (withoutDateFilters) {
    label += ` IN THE LAST ${hasAnalytics ? 'YEAR' : 'MONTH'}`;
  }

  return label;
}

export interface ReportListHeaderProps {
  hasAnalytics: boolean;
  isLoading?: boolean;
  isDownloadDisabled: boolean;
  filters: ReportListFilters;
  sortAndGroupProps: ReportSortGroupByButtonMenuProps;
  previousSearchText: string;
  isAdvancedFiltersActive: boolean;
  onAdvancedFiltersSwitchChange: (active: boolean) => void;
  onDownload: () => void;
  onQuickSearch: (searchText: string) => void;
  onClearSearchText: () => void;
  onFiltersChange: (newFilters: ReportListFilters) => void;
  onOpenFilters: () => void;
  reportsCount: ReportsCount;
}

export const ReportListHeader = ({
  hasAnalytics,
  isLoading,
  isDownloadDisabled,
  filters,
  sortAndGroupProps,
  previousSearchText,
  isAdvancedFiltersActive,
  onAdvancedFiltersSwitchChange,
  onDownload,
  onQuickSearch,
  onClearSearchText,
  onFiltersChange,
  onOpenFilters,
  reportsCount
}: ReportListHeaderProps): React.ReactElement<ReportListHeaderProps> => {
  const filtersConfig = useContext(StateConfigContext).stateConfig
    ?.filters as ReportFiltersConfig;

  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const refMoreOptionsBtn = useRef<HTMLDivElement>(null);

  const onChipDelete = (chip: FilterChip) => {
    const newFilters = chip.onDelete(filters as ReportListFilters);
    onFiltersChange(newFilters);
  };

  const chips = getChips(filtersConfig, filters);

  const withoutFilters = isValueEmpty(filters);
  const withoutDateFilters: boolean =
    !isValueEmpty(filters) &&
    !(filters.general_information?.date as DateRange)?.dateFrom;

  const noReportsLabel: boolean =
    !withoutFilters && hasAnalytics && !withoutDateFilters;

  const countSuffix = getLabelSuffix(
    hasAnalytics,
    withoutFilters,
    withoutDateFilters
  );

  const onOptionClick = (value: string) => {
    if (value === 'download') {
      onDownload();
    } else if (value === 'filter') {
      onOpenFilters();
    } else if (value === 'sortAndGroup') {
      setAnchorEl(refMoreOptionsBtn.current);
    }
  };

  return (
    <div className={styles.reportListHeader}>
      <div className={styles.content}>
        <div
          className={`${styles.labels} 
            ${noReportsLabel ? styles.noReportsLabel : ''} 
            ${!isAdvancedFiltersActive ? styles.quickSearchLabel : ''}
          `}
        >
          {isAdvancedFiltersActive && (
            <Fragment>
              {!noReportsLabel && (
                <ReportsCountLabel
                  count={
                    hasAnalytics && !withoutFilters ? 'none' : reportsCount
                  }
                  suffix={countSuffix}
                />
              )}

              <FilterChipList chips={chips} onDelete={onChipDelete} />
            </Fragment>
          )}
          {!isAdvancedFiltersActive && (
            <ReportsCountLabel count="all" suffix="REPORTS" />
          )}
        </div>

        {!isAdvancedFiltersActive && (
          <div className={styles.quickSearch}>
            <SearchFilter
              className={styles.searchFilter}
              placeholder="Search"
              disable={isLoading}
              previousSearchText={previousSearchText}
              isAdvancedFiltersActive={isAdvancedFiltersActive}
              onAdvancedFiltersSwitchChange={(active: boolean) => {
                onAdvancedFiltersSwitchChange(active);
              }}
              onSearchText={(searchText: string) => {
                onQuickSearch(searchText);
              }}
              onSelect={(value: string) => {
                onQuickSearch(value);
              }}
              onClearSearchText={onClearSearchText}
            />
          </div>
        )}

        <div className={styles.actions}>
          {isAdvancedFiltersActive && (
            <FilterByButton
              className={styles.filterBtn}
              filterCount={chips.length}
              hasAnalytics={hasAnalytics}
              onClick={onOpenFilters}
            />
          )}

          <ReportSortGroupByButton
            className={styles.sortGroupBtn}
            {...sortAndGroupProps}
          />

          <MoreOptionsButton
            ref={refMoreOptionsBtn}
            isAdvancedFiltersActive={isAdvancedFiltersActive}
            isDownloadDisabled={isDownloadDisabled}
            sortLabel={`${upperFirstChar(
              sortAndGroupProps.groupedBy
            )} - ${upperFirstChar(sortAndGroupProps.sortBy)}`}
            onClick={onOptionClick}
          />

          <ReportSortGroupByButtonPopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            {...sortAndGroupProps}
          />
        </div>
      </div>
    </div>
  );
};
